import React, { useState } from 'react';
import { X, Tag as TagIcon, Pencil, Trash2, Check, Plus } from 'lucide-react';
import TagChip from './TagChip';
import { TAG_COLORS, safeTagColor, suggestTagColor } from './tagPalette';
import './Tags.css';

/*
 * Tag maintenance for a project (WR-010).
 *
 * Reached from the board header rather than Project Settings: settings are
 * owner-only (the project PUT enforces it), and tags are edited by whoever is
 * doing the work. The tag routes are member-level for the same reason.
 *
 * This is the maintenance surface — rename, recolour, delete. Creating a tag
 * while tagging something happens inline in the task modal's picker; the add
 * row at the foot of the list is the deliberate-maintenance equivalent.
 *
 * Structure deliberately mirrors the Completed view (CompletedTasks.js), the
 * app's other list-in-a-modal: header band → tab strip → scrolling content,
 * with rows as leading icon + title/meta + trailing actions. No footer band —
 * Completed has none either, so the add row lives at the end of the list.
 *
 * Props mirror the board's own handlers so every change lands in the single
 * copy of the project the board holds, and chips update everywhere at once.
 */

const TABS = [
  { id: 'all',    label: 'All tags' },
  { id: 'used',   label: 'In use' },
  { id: 'unused', label: 'Unused' },
];

const EMPTY_COPY = {
  all:    'No tags in this project yet. Add one below, or type a new name straight into a task’s tag picker — that is the quicker path while you are working.',
  used:   'None of these tags are on a task yet. Attach one from a task’s tag picker.',
  unused: 'Every tag in this project is on at least one task.',
};

function TagManager({ tags = [], counts = {}, onClose, onCreate, onUpdate, onDelete }) {
  const [tab, setTab] = useState('all');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName]   = useState('');
  const [editColor, setEditColor] = useState('slate');

  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState(() => suggestTagColor(tags.map(t => t.color)));

  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);

  const usage = (tag) => counts[String(tag._id)] || 0;
  const inUse = tags.filter(t => usage(t) > 0).length;

  const visible = tags.filter(t => (
    tab === 'used'   ? usage(t) > 0 :
    tab === 'unused' ? usage(t) === 0 :
    true
  ));

  const fail = (err, fallback) => setError(err.response?.data?.error || fallback);

  const startEdit = (tag) => {
    setEditingId(tag._id);
    setEditName(tag.name);
    setEditColor(tag.color);
    setError(null);
  };

  const cancelEdit = () => { setEditingId(null); setError(null); };

  const saveEdit = async (tag) => {
    const name = editName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await onUpdate(tag._id, { name, color: editColor });
      setEditingId(null);
    } catch (err) {
      fail(err, 'Could not save that tag.');
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(name, newColor);
      setNewName('');
      // Next suggestion accounts for the colour just used.
      setNewColor(suggestTagColor([...tags.map(t => t.color), newColor]));
    } catch (err) {
      fail(err, 'Could not create that tag.');
    } finally {
      setBusy(false);
    }
  };

  // The count is the point of the confirm: a tag can be on tasks that are
  // archived and not on the board at all, so the number is often larger than
  // anything the user can see.
  const remove = async (tag) => {
    const used = usage(tag);
    const detail = used === 0
      ? 'It is not on any task.'
      : `Used on ${used} task${used === 1 ? '' : 's'} — archived ones included. The tag is removed from all of them.`;
    if (!window.confirm(`Delete the tag "${tag.name}"?\n\n${detail}`)) return;

    setBusy(true);
    setError(null);
    try {
      await onDelete(tag._id);
    } catch (err) {
      fail(err, 'Could not delete that tag.');
    } finally {
      setBusy(false);
    }
  };

  const Swatches = ({ value, onPick }) => (
    <div className="tag-swatches">
      {TAG_COLORS.map(c => (
        <button
          key={c.id}
          type="button"
          title={c.label}
          aria-label={c.label}
          className={`tag-swatch tag-swatch-${c.id} ${value === c.id ? 'selected' : ''}`}
          onClick={() => onPick(c.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tag-manager-modal" onClick={e => e.stopPropagation()}>
        <div className="tag-manager-header">
          <div className="tag-manager-title">
            <TagIcon size={20} />
            <div>
              <h2>Tags</h2>
              <p>
                {`${tags.length} ${tags.length === 1 ? 'tag' : 'tags'} in this project`}
                {inUse > 0 ? ` · ${inUse} in use` : ''}
              </p>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="tag-manager-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`tag-manager-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="tag-manager-content">
          {error && (
            <div className="tag-manager-error" role="alert">
              <span>{error}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {visible.length === 0 ? (
            <div className="tag-manager-empty">
              <TagIcon size={32} />
              <h3>Nothing here yet</h3>
              <p>{EMPTY_COPY[tab]}</p>
            </div>
          ) : (
            <ul className="tag-manager-list">
              {visible.map(tag => {
                const used = usage(tag);
                const isEditing = editingId === tag._id;

                return (
                  <li key={tag._id} className={`tag-manager-row${isEditing ? ' is-editing' : ''}`}>
                    <TagIcon
                      size={16}
                      className={`tag-manager-row-icon tag-icon-${safeTagColor(tag.color)}`}
                    />

                    {isEditing ? (
                      <>
                        <div className="tag-manager-row-main">
                          <input
                            className="tag-manager-input"
                            value={editName}
                            maxLength={30}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEdit(tag);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                          />
                          <div className="tag-manager-row-meta">
                            <Swatches value={editColor} onPick={setEditColor} />
                          </div>
                        </div>
                        <button
                          className="btn btn-primary btn-sm tag-manager-action"
                          onClick={() => saveEdit(tag)}
                          disabled={busy || !editName.trim()}
                        >
                          <Check size={14} />
                          Save
                        </button>
                        <button className="btn btn-ghost btn-sm tag-manager-action" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="tag-manager-row-main">
                          <span className="tag-manager-row-title">
                            <TagChip tag={tag} />
                          </span>
                          <div className="tag-manager-row-meta">
                            <span>
                              {used === 0
                                ? 'Not on any task'
                                : `Used on ${used} ${used === 1 ? 'task' : 'tasks'}`}
                            </span>
                            {used > 0 && <span>Archived tasks included</span>}
                          </div>
                        </div>
                        <button
                          className="btn btn-ghost btn-sm tag-manager-action"
                          onClick={() => startEdit(tag)}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm tag-manager-action tag-manager-danger"
                          onClick={() => remove(tag)}
                          disabled={busy}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Creation sits at the end of the list rather than in a footer
              band — Completed has no footer, and this is the maintenance
              equivalent of the picker's inline "Create tag". */}
          <div className="tag-manager-row tag-manager-add">
            <Plus size={16} className="tag-manager-row-icon" />
            <div className="tag-manager-row-main">
              <input
                className="tag-manager-input"
                placeholder="New tag name..."
                value={newName}
                maxLength={30}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') create(); }}
              />
              <div className="tag-manager-row-meta">
                <Swatches value={newColor} onPick={setNewColor} />
              </div>
            </div>
            <button
              className="btn btn-primary btn-sm tag-manager-action"
              onClick={create}
              disabled={busy || !newName.trim()}
            >
              <Plus size={14} />
              Add tag
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TagManager;
